import clsx from 'clsx'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import type SvgIcon from 'LuxKit/SvgIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS, TOUR_STYLED_PRODUCT_TYPE_TITLES } from 'constants/tours'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled.div`
  position: relative;
  padding: ${rem(12)} ${rem(20)} ${rem(28)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(20)} ${rem(28)};
  }

  &.is-current {
    padding-top: ${rem(20)};
    border-color: ${props => props.theme.palette.neutral.default.one}
  }
`

const CurrentLabel = styled(StructureLabel)`
  position: absolute;
  top: -1px;
  left: -1px;
  transform: translateY(-50%);
`

interface TourStyleBenefit {
  Icon: typeof SvgIcon
  description: string
}

interface TourStyleContent {
  subtitle: string
  benefits: Array<TourStyleBenefit>
}

interface Props {
  productType: Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2SignatureSeriesProductType | App.Tours.V2InsiderSeriesProductType>
  isCurrent: boolean
}

function TourStyledNonSignatureProductTypeInfoSheet({ productType, isCurrent }: Props) {
  const intl = useIntl()

  const tourStylesBenefits = useMemo<Record<Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2SignatureSeriesProductType | App.Tours.V2InsiderSeriesProductType>, TourStyleContent>>(() => ({
    deluxe_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'tbnudKpZh6',
          defaultMessage: 'Discover the highlights, effortlessly',
          description: 'Subtitle for deluxe tour style describing easy highlight discovery',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS.deluxe_tour },
        {
          Icon: LineUsersAltIcon,
          description: intl.formatMessage(
            {
              id: '+DdgBFavQ2',
              defaultMessage: 'Max 24 travellers',
              description: 'Benefit showing maximum group size for deluxe tours',
            },
          ),
        },
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'nStO9BAu9v',
              defaultMessage: 'Unmissable experiences',
              description: 'Benefit highlighting must-see experiences included in deluxe tours',
            },
          ),
        },
      ],
    },
    premium_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'VXZd0ub0+F',
          defaultMessage: 'Elevated experiences, elevated accommodation',
          description: 'Subtitle for premium tour style emphasising elevated quality',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS.premium_tour },
        {
          Icon: LineUsersAltIcon,
          description: intl.formatMessage(
            {
              id: 'KqGbmIaXXK',
              defaultMessage: 'Max 16 travellers',
              description: 'Benefit showing maximum group size for premium tours',
            },
          ),
        },
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'y/fySsJbSN',
              defaultMessage: 'Dedicated agent',
              description: 'Benefit highlighting dedicated travel agent for premium tours',
            },
          ),
        },
      ],
    },
    ultra_lux_tour: {
      subtitle: intl.formatMessage(
        {
          id: 'KMp+kqdyfb',
          defaultMessage: 'Only the best, from start to finish',
          description: 'Subtitle for ultra lux tour style emphasising top-tier quality throughout',
        },
      ),
      benefits: [
        { Icon: LineBedIcon, description: TOUR_STYLED_PRODUCT_TYPE_HOTEL_BENEFITS.ultra_lux_tour },
        {
          Icon: LineUsersAltIcon,
          description: intl.formatMessage(
            {
              id: 'w4bQi0z1Cv',
              defaultMessage: 'Max 12 travellers',
              description: 'Benefit showing maximum group size for ultra lux tours',
            },
          ),
        },
        {
          Icon: LineMapIcon,
          description: intl.formatMessage(
            {
              id: 'mEMVMdkeBd',
              defaultMessage: 'Signature dining experiences',
              description: 'Benefit highlighting exclusive dining experiences in ultra lux tours',
            },
          ),
        },
      ],
    },
    custom_tour: {
      subtitle: '',
      benefits: [],
    },
  }), [intl])

  const content = tourStylesBenefits[productType]

  const currentTourLabel = intl.formatMessage(
    {
      id: '7yXkfuu8Xl',
      defaultMessage: 'Current tour',
      description: 'Label indicating which tour style the user is currently viewing',
    },
  )

  return <Container className={clsx({ 'is-current': isCurrent })}>
    {isCurrent && <CurrentLabel variant="dark">{currentTourLabel}</CurrentLabel>}
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <Heading variant="heading4" colour="neutral-one">
          {TOUR_STYLED_PRODUCT_TYPE_TITLES[productType]}
        </Heading>
        <BodyText variant="medium" colour="neutral-two">
          {content.subtitle}
        </BodyText>
      </Group>
      <Group direction="vertical" gap={8}>
        {content.benefits.map((benefit) => <BodyTextBlock
          key={benefit.description}
          colour="neutral-two"
          variant="medium"
          startIcon={<benefit.Icon />}
        >
          {benefit.description}
        </BodyTextBlock>)}
      </Group>
    </Group>
  </Container>
}

export default TourStyledNonSignatureProductTypeInfoSheet
